import { useState, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DataTable } from '@/components/ui/table/data-table';
import { DataTableSkeleton } from '@/components/ui/table/data-table-skeleton';
import { AlertModal } from '@/components/modal/alert-modal';
import { Plus, Users, Mail, ChevronLeft, ChevronRight } from 'lucide-react';
import { showToast } from '@/components/ui/show-toast';
import PageContainer from '@/components/layout/page-container';
import { Heading } from '@/components/ui/heading';
import { useAuthStore } from '@/store/auth';
import { createUserColumns } from '@/modules/users/user-table/columns';
import { createInvitationColumns } from '@/modules/users/invitation-table/columns';
import { SwitchRoleModal } from '@/modules/users/modals/switch-role-modal';
import { InvitationModal } from '@/modules/users/modals/invitation-modal';
import { 
  useGetOrganisationUsers, 
  useUpdateOrganisationUserRole, 
  useDeleteOrganisationUser, 
  useGetOrganisationInvitations,
} from '@/services/organisations/queries';
import { 
    useGetWorkspaceUsers, 
    useUpdateWorkspaceUserRole, 
    useDeleteWorkspaceUser, 
    useGetWorkspaceInvitations 
} from '@/services/workspaces/queries'; 
import { useDeleteInvitation } from '@/services/invitations/queries';
import type { User, Invitation } from '@/services/types';

type UserWithRole = User & {
  role: string;
};

export default function UsersPage() {
  const { user, context } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'users' | 'invitations'>('users');
  const [selectedUser, setSelectedUser] = useState<UserWithRole | null>(null);
  const [showSwitchRoleModal, setShowSwitchRoleModal] = useState(false);
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'user' | 'invitation'; id: string; name: string } | null>(null);
  
  // Pagination state for server-side pagination
  const [userPage, setUserPage] = useState(1);
  const [invitationPage, setInvitationPage] = useState(1);
  const itemsPerPage = 5;

  // Get IDs based on context
  const organisationId = user?.organisation?.id;
  const workspaceId = user?.workspace?.workspaceId || user?.organisation?.workspaces?.[0];

  // Check if current user is admin
  const isAdmin = useMemo(() => {
    if (context === 'organisation') {
      return user?.organisation?.role === 'org_admin';
    } else {
      return user?.workspace?.role === 'workspace_admin';
    }
  }, [context, user]);

  // User queries based on context
  const { data: organisationUsers = [], isLoading: usersLoading } = useGetOrganisationUsers(
    organisationId || '',
    {
      page: userPage,
      limit: itemsPerPage,
    },
    {
      enabled: !!organisationId && context === 'organisation'
    }
  );
   
  const { data: workspaceUsers = [], isLoading: workspaceUsersLoading } = useGetWorkspaceUsers(
    workspaceId || '',
    {
      page: userPage,
      limit: itemsPerPage,
    },
    {
      enabled: !!workspaceId && context === 'workspace'
    }
  );

  // Invitation queries based on context
  const { data: organisationInvitations = [], isLoading: invitationsLoading } = useGetOrganisationInvitations(
    organisationId || '',
    {
      page: invitationPage,
      limit: itemsPerPage,
    },
    {
      enabled: !!organisationId && context === 'organisation'
    }
  );

  const { data: workspaceInvitations = [], isLoading: workspaceInvitationsLoading } = useGetWorkspaceInvitations(
    workspaceId || '',
    {
      page: invitationPage,
      limit: itemsPerPage,
    },
    {
      enabled: !!workspaceId && context === 'workspace'
    }
  );

  // Mutations for organisation context
  const { mutate: updateOrgUserRole, isPending: isUpdatingOrgRole } = useUpdateOrganisationUserRole(
    organisationId || '',
    () => {
      setShowSwitchRoleModal(false);
      showToast('success', 'User role updated successfully');
    },
    (error) => {
      showToast('error', error?.response?.data?.message || 'Failed to update user role');
    }
  );

  const { mutate: deleteOrgUser, isPending: isDeletingOrgUser } = useDeleteOrganisationUser(
    organisationId || '',
    () => {
      showToast('success', 'User deleted successfully');
      setShowDeleteModal(false);
    },
    (error) => {
      showToast('error', error?.response?.data?.message || 'Failed to delete user');
    }
  );

  // Mutations for workspace context
  const { mutate: updateWorkspaceUserRole, isPending: isUpdatingWorkspaceRole } = useUpdateWorkspaceUserRole(
    workspaceId || '',
    () => {
      showToast('success', 'User role updated successfully');
      setShowSwitchRoleModal(false);
    },
    (error) => {
      showToast('error', error?.response?.data?.message || 'Failed to update user role');
    }
  );

  const { mutate: deleteWorkspaceUser, isPending: isDeletingWorkspaceUser } = useDeleteWorkspaceUser(
    workspaceId || '',
    () => {
      showToast('success', 'User deleted successfully');
      setShowDeleteModal(false);
    },
    (error) => {
      showToast('error', error?.response?.data?.message || 'Failed to delete user');
    }
  );

  // Delete invitation mutation
  const { mutate: deleteInvitationMutation, isPending: isDeletingInvitation } = useDeleteInvitation(
    () => {
      showToast('success', 'Invitation deleted successfully');
      setShowDeleteModal(false);
    },
    (error) => {
      showToast('error', error?.response?.data?.message || 'Failed to delete invitation');
    }
  );

  // Get current data based on context
  const currentUsers = (context === 'organisation' ? organisationUsers : workspaceUsers) as any[];
  const currentInvitations = (context === 'organisation' ? organisationInvitations : workspaceInvitations) as any[];
  const isLoadingUsers = context === 'organisation' ? usersLoading : workspaceUsersLoading;
  const isLoadingInvitations = context === 'organisation' ? invitationsLoading : workspaceInvitationsLoading;

  // Transform users data to include role
  const usersWithRole: UserWithRole[] = useMemo(() => {
    return currentUsers.map((user: any) => ({
      ...user,
      role: context === 'organisation' ? user.organisation.role : user.workspace.role
    }));
  }, [currentUsers, context]);

  // Callbacks for user actions
  const handleSwitchRole = useCallback((user: UserWithRole) => {
    setSelectedUser(user);
    setShowSwitchRoleModal(true);
  }, []);

  const handleDeleteUser = useCallback((user: UserWithRole) => {
    setDeleteTarget({
      type: 'user',
      id: user.id,
      name: user.fullName || user.email
    });
    setShowDeleteModal(true);
  }, []);

  const handleDeleteInvitation = useCallback((invitation: Invitation & { id?: string }) => {
    setDeleteTarget({
      type: 'invitation',
      id: invitation.id || invitation.email,
      name: invitation.email
    });
    setShowDeleteModal(true);
  }, []);

  const handleConfirmRoleUpdate = useCallback((userId: string, newRole: string) => {
    if (context === 'organisation') {
      updateOrgUserRole({ userId, role: newRole });
    } else {
      updateWorkspaceUserRole({ userId, role: newRole });
    }
  }, [context, updateOrgUserRole, updateWorkspaceUserRole]);

  const handleConfirmDelete = useCallback(() => {
    if (!deleteTarget) return;

    if (deleteTarget.type === 'user') {
      if (context === 'organisation') {
        deleteOrgUser(deleteTarget.id);
      } else {
        deleteWorkspaceUser(deleteTarget.id);
      }
    } else if (deleteTarget.type === 'invitation') {
      deleteInvitationMutation({ id: deleteTarget.id, context: context || 'organisation' });
    }
  }, [deleteTarget, context, deleteOrgUser, deleteWorkspaceUser, deleteInvitationMutation]);

  // Create columns
  const userColumns = useMemo(() => 
    createUserColumns({
      onSwitchRole: handleSwitchRole,
      onDeleteUser: handleDeleteUser,
      isAdmin
    }), 
    [handleSwitchRole, handleDeleteUser, isAdmin]
  );

  const invitationColumns = useMemo(() => 
    createInvitationColumns({
      onDeleteInvitation: handleDeleteInvitation,
      isAdmin
    }), 
    [handleDeleteInvitation, isAdmin]
  );

  const isLoading = isLoadingUsers || isLoadingInvitations;

  return (
    <PageContainer scrollable={true}>
      <div className='mb-5 flex items-center justify-between px-4'>
        <Heading title='Users' description='Manage users and invitations' />
        {isAdmin && (
          <Button onClick={() => setShowInvitationModal(true)}>
            <Plus className='mr-2 h-4 w-4' />
            Send Invitation
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'users' | 'invitations')} className='h-full space-y-4'>
        <TabsList className='w-full border-b px-5'>
          <TabsTrigger value='users' className='flex items-center gap-2'>
            <Users className='h-4 w-4' />
            Users ({usersWithRole.length})
          </TabsTrigger>
          <TabsTrigger value='invitations' className='flex items-center gap-2'>
            <Mail className='h-4 w-4' />
            Invitations ({currentInvitations.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value='users' className='h-full space-y-4'>
          <div className='px-4'>
            {isLoading ? (
              <DataTableSkeleton />
            ) : (
              <>
                <DataTable
                  columns={userColumns}
                  data={usersWithRole}
                  totalItems={usersWithRole.length}
                  emptyState={
                    <div className='flex h-full w-full flex-col items-center justify-center py-8'>
                      <Users className='h-8 w-8 text-muted-foreground' />
                      <p className='text-muted-foreground'>No users found</p>
                    </div>
                  }
                  paginationOff={true}
                />
                {usersWithRole.length > 0 && (
                  <div className='flex items-center justify-between px-4 py-3 border-t'>
                    <div className='text-sm text-muted-foreground'>
                      Page {userPage}
                    </div>
                    <div className='flex items-center space-x-2'>
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() => setUserPage(userPage - 1)}
                        disabled={userPage <= 1}
                      >
                        <ChevronLeft className='h-4 w-4' />
                        Previous
                      </Button>
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() => setUserPage(userPage + 1)}
                        disabled={usersWithRole.length < itemsPerPage}
                      >
                        Next
                        <ChevronRight className='h-4 w-4' />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value='invitations' className='h-full space-y-4'>
          <div className='px-4'>
            {isLoading ? (
              <DataTableSkeleton />
            ) : (
              <>
                <DataTable
                  columns={invitationColumns}
                  data={currentInvitations}
                  totalItems={currentInvitations.length}
                  emptyState={
                    <div className='flex h-full w-full flex-col items-center justify-center py-8'>
                      <Mail className='h-8 w-8 text-muted-foreground' />
                      <p className='text-muted-foreground'>No invitations found</p>
                    </div>
                  }
                  paginationOff={true}
                />
                {currentInvitations.length > 0 && (
                  <div className='flex items-center justify-between px-4 py-3 border-t'>
                    <div className='text-sm text-muted-foreground'>
                      Page {invitationPage}
                    </div>
                    <div className='flex items-center space-x-2'>
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() => setInvitationPage(invitationPage - 1)}
                        disabled={invitationPage <= 1}
                      >
                        <ChevronLeft className='h-4 w-4' />
                        Previous
                      </Button>
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() => setInvitationPage(invitationPage + 1)}
                        disabled={currentInvitations.length < itemsPerPage}
                      >
                        Next
                        <ChevronRight className='h-4 w-4' />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <SwitchRoleModal
        open={showSwitchRoleModal}
        onOpenChange={setShowSwitchRoleModal}
        user={selectedUser}
        onConfirm={handleConfirmRoleUpdate}
        isLoading={isUpdatingOrgRole || isUpdatingWorkspaceRole}
        context={context || 'organisation'}
      />

      <InvitationModal
        open={showInvitationModal}
        onOpenChange={setShowInvitationModal}
        context={context || 'organisation'}
        organisationId={organisationId}
        workspaceId={workspaceId}
        onSuccess={() => {
          // Refresh data will be handled by query invalidation
        }}
      />

      <AlertModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        loading={isDeletingOrgUser || isDeletingWorkspaceUser || isDeletingInvitation}
        title={`Delete ${deleteTarget?.type === 'user' ? 'User' : 'Invitation'}`}
        description={`Are you sure you want to delete ${deleteTarget?.name}? This action cannot be undone.`}
      />
    </PageContainer>
  );
}